import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  listAdminLayouts,
  listAdminFamilies,
  createLayout,
  updateLayout,
  deleteLayout,
  type AdminLayout,
  type AdminFamily,
} from '../../lib/adminApi';
import { formatCode, formatCents, formatDimensions } from '../../lib/format';
import { AdminPageShell, cls, ChevronDown } from './shared';

export function LayoutsPage() {
  const [layouts, setLayouts] = useState<AdminLayout[]>([]);
  const [families, setFamilies] = useState<AdminFamily[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterFamily, setFilterFamily] = useState<number | null>(null);

  // Create form state
  const [showCreate, setShowCreate] = useState(false);
  const [newFamilyId, setNewFamilyId] = useState<number | ''>('');
  const [newCode, setNewCode] = useState('');
  const [newName, setNewName] = useState('');
  const [newWidth, setNewWidth] = useState('');
  const [newDepth, setNewDepth] = useState('');
  const [saving, setSaving] = useState(false);

  function load() {
    Promise.all([listAdminLayouts(), listAdminFamilies()])
      .then(([lr, fr]) => {
        setLayouts(lr.layouts);
        setFamilies(fr.families);
        if (fr.families.length > 0 && newFamilyId === '') {
          setNewFamilyId(fr.families[0]!.id);
        }
        setLoading(false);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Failed to load');
        setLoading(false);
      });
  }

  useEffect(load, []);

  async function handleCreate() {
    if (!newCode.trim() || newFamilyId === '') return;
    setSaving(true);
    try {
      await createLayout({
        family_id: newFamilyId as number,
        code: newCode.trim(),
        name: newName.trim() || undefined,
        width_inches: newWidth ? parseFloat(newWidth) : undefined,
        depth_inches: newDepth ? parseFloat(newDepth) : undefined,
      });
      setShowCreate(false);
      setNewCode('');
      setNewName('');
      setNewWidth('');
      setNewDepth('');
      load();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Error creating layout');
    } finally {
      setSaving(false);
    }
  }

  const visible = filterFamily ? layouts.filter((l) => l.familyId === filterFamily) : layouts;

  if (loading) return <AdminPageShell title="Layouts"><p className="text-vari-gray text-sm">Loading…</p></AdminPageShell>;
  if (error) return <AdminPageShell title="Layouts"><p className="text-red-600 text-sm">{error}</p></AdminPageShell>;

  return (
    <AdminPageShell
      title={`Layouts (${layouts.length})`}
      actions={
        <button
          onClick={() => setShowCreate((v) => !v)}
          className={cls.btnPrimary}
        >
          + New Layout
        </button>
      }
      stickyTop={
        <>
      {showCreate && (
        <div className={cls.formPanel}>
          <p className="text-sm font-medium text-vari-navy mb-3">New Layout</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 mb-3">
            <div>
              <label className="text-xs text-vari-gray block mb-1">Family <span className="text-red-500">*</span></label>
              <select
                className={cls.select + ' w-full'}
                value={newFamilyId}
                onChange={(e) => setNewFamilyId(parseInt(e.target.value, 10))}
              >
                {families.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-vari-gray block mb-1">Code <span className="text-red-500">*</span></label>
              <input
                className={cls.input}
                placeholder="e.g. OO-A1"
                value={newCode}
                onChange={(e) => setNewCode(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-vari-gray block mb-1">Name</label>
              <input
                className={cls.input}
                placeholder="Optional display name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-vari-gray block mb-1">Width (in)</label>
              <input
                type="number"
                min="0"
                step="0.5"
                className={cls.input}
                placeholder="e.g. 144"
                value={newWidth}
                onChange={(e) => setNewWidth(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-vari-gray block mb-1">Depth (in)</label>
              <input
                type="number"
                min="0"
                step="0.5"
                className={cls.input}
                placeholder="e.g. 96"
                value={newDepth}
                onChange={(e) => setNewDepth(e.target.value)}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => void handleCreate()}
              disabled={saving || !newCode.trim() || newFamilyId === ''}
              className={cls.btnSmPrimary}
            >
              {saving ? 'Saving…' : 'Create'}
            </button>
            <button
              onClick={() => setShowCreate(false)}
              className={cls.btnGhost}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        <label className="text-sm text-vari-gray">Filter family:</label>
        <div className="relative inline-flex items-center">
          <select
            className={cls.select + ' appearance-none pr-7'}
            value={filterFamily ?? ''}
            onChange={(e) => setFilterFamily(e.target.value ? parseInt(e.target.value, 10) : null)}
          >
            <option value="">All</option>
            {families.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-2 h-3 w-3 text-vari-gray/40 pointer-events-none" />
        </div>
        <span className="text-xs text-vari-gray ml-auto">{visible.length} shown</span>
      </div>
        </>
      }
    >

      <div className={cls.tableWrap}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-vari-soft-gray">
              <tr>
                <th className={cls.th}>Code</th>
                <th className={cls.th}>Family</th>
                <th className={cls.th}>Name</th>
                <th className={cls.th}>Dimensions</th>
                <th className={cls.th}>Price</th>
                <th className={cls.thCenter}>Active</th>
                <th className={cls.th}>Renders</th>
                <th className={cls.th}>BOM</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {visible.map((l, i) => (
                <LayoutRow
                  key={l.id}
                  layout={l}
                  families={families}
                  striped={i % 2 === 1}
                  onSaved={load}
                />
              ))}
              {visible.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-6 text-center text-vari-gray text-sm">
                    No layouts.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminPageShell>
  );
}

function LayoutRow({
  layout,
  families,
  striped,
  onSaved,
}: {
  layout: AdminLayout;
  families: AdminFamily[];
  striped: boolean;
  onSaved: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Name editing
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(layout.name ?? '');

  // Dimensions editing
  const [editingDims, setEditingDims] = useState(false);
  const [widthValue, setWidthValue] = useState(layout.widthInches?.toString() ?? '');
  const [depthValue, setDepthValue] = useState(layout.depthInches?.toString() ?? '');

  async function save(updates: Parameters<typeof updateLayout>[1]) {
    setSaving(true);
    try {
      await updateLayout(layout.id, updates);
      onSaved();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to update');
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive() {
    await save({ is_active: !layout.isActive });
  }

  async function commitName() {
    setEditingName(false);
    const trimmed = nameValue.trim();
    if (trimmed !== (layout.name ?? '')) {
      await save({ name: trimmed || null });
    }
  }

  async function commitDims() {
    setEditingDims(false);
    const w = widthValue.trim() ? parseFloat(widthValue) : null;
    const d = depthValue.trim() ? parseFloat(depthValue) : null;
    if (w !== layout.widthInches || d !== layout.depthInches) {
      await save({ width_inches: w, depth_inches: d });
    }
  }

  async function handleDelete() {
    try {
      await deleteLayout(layout.id);
      onSaved();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to delete');
      setConfirmDelete(false);
    }
  }

  const cellBase = 'px-3 py-2.5 text-xs';

  return (
    <tr className={`${cls.tr(striped)} ${!layout.isActive ? 'opacity-50' : ''}`}>
      <td className={`${cellBase} font-mono text-vari-gray`}>{formatCode(layout.code)}</td>

      {/* Family — select dropdown */}
      <td className={cellBase}>
        <div className="relative inline-flex items-center gap-1">
          <select
            className="appearance-none text-xs text-vari-gray bg-transparent border-none cursor-pointer pr-4 focus:outline-none hover:text-vari-navy transition-colors disabled:cursor-not-allowed"
            value={layout.familyId}
            disabled={saving}
            onChange={(e) => void save({ family_id: parseInt(e.target.value, 10) })}
          >
            {families.map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-0 h-3 w-3 text-vari-gray/40 pointer-events-none" />
        </div>
      </td>

      {/* Name — click to edit */}
      <td className={cellBase}>
        {editingName ? (
          <input
            autoFocus
            type="text"
            value={nameValue}
            onChange={(e) => setNameValue(e.target.value)}
            onBlur={() => void commitName()}
            onKeyDown={(e) => { if (e.key === 'Enter') void commitName(); if (e.key === 'Escape') { setNameValue(layout.name ?? ''); setEditingName(false); } }}
            className="text-xs border border-vari-soft-gray rounded px-1 py-0.5 w-44 focus:outline focus:outline-1 focus:outline-vari-navy"
          />
        ) : (
          <span
            onClick={() => { setNameValue(layout.name ?? ''); setEditingName(true); }}
            className="cursor-pointer hover:bg-gray-100 rounded px-1 -ml-1"
            title="Click to edit"
          >
            {layout.name ?? <span className="text-vari-gray/50 italic">—</span>}
          </span>
        )}
      </td>

      {/* Dimensions — click to edit */}
      <td className={`${cellBase} text-vari-gray`}>
        {editingDims ? (
          <span className="flex items-center gap-1">
            <input
              autoFocus
              type="number"
              min="0"
              step="0.5"
              value={widthValue}
              onChange={(e) => setWidthValue(e.target.value)}
              onBlur={() => void commitDims()}
              onKeyDown={(e) => { if (e.key === 'Enter') void commitDims(); if (e.key === 'Escape') { setWidthValue(layout.widthInches?.toString() ?? ''); setDepthValue(layout.depthInches?.toString() ?? ''); setEditingDims(false); } }}
              placeholder="W"
              className="text-xs border border-vari-soft-gray rounded px-1 py-0.5 w-14 focus:outline focus:outline-1 focus:outline-vari-navy"
            />
            <span className="text-vari-gray/50">×</span>
            <input
              type="number"
              min="0"
              step="0.5"
              value={depthValue}
              onChange={(e) => setDepthValue(e.target.value)}
              onBlur={() => void commitDims()}
              onKeyDown={(e) => { if (e.key === 'Enter') void commitDims(); if (e.key === 'Escape') { setWidthValue(layout.widthInches?.toString() ?? ''); setDepthValue(layout.depthInches?.toString() ?? ''); setEditingDims(false); } }}
              placeholder="D"
              className="text-xs border border-vari-soft-gray rounded px-1 py-0.5 w-14 focus:outline focus:outline-1 focus:outline-vari-navy"
            />
            <span className="text-vari-gray/60 text-xs">in</span>
          </span>
        ) : (
          <span
            onClick={() => { setWidthValue(layout.widthInches?.toString() ?? ''); setDepthValue(layout.depthInches?.toString() ?? ''); setEditingDims(true); }}
            className="cursor-pointer hover:bg-gray-100 rounded px-1 -ml-1"
            title="Click to edit"
          >
            {formatDimensions(layout.widthInches, layout.depthInches)}
          </span>
        )}
      </td>

      <td className={cellBase}>{formatCents(layout.totalPriceCents)}</td>
      <td className="px-3 py-2.5 text-center">
        <input
          type="checkbox"
          checked={layout.isActive}
          disabled={saving}
          onChange={() => void toggleActive()}
          className="accent-vari-navy cursor-pointer"
        />
      </td>
      <td className="px-3 py-2.5">
        <Link
          to={`/admin/layouts/${layout.id}/renders`}
          className="text-xs text-vari-blue-accessible hover:underline"
        >
          {layout.renderCount} renders
        </Link>
      </td>
      <td className="px-3 py-2.5">
        <Link
          to={`/admin/layouts/${layout.id}/bom`}
          className="text-xs text-vari-blue-accessible hover:underline"
        >
          {layout.bomCount} items
        </Link>
      </td>
      <td className="px-3 py-2.5 text-right">
        {confirmDelete ? (
          <span className="space-x-1">
            <button
              onClick={() => void handleDelete()}
              className={cls.btnConfirm}
            >
              Confirm?
            </button>
            <button onClick={() => setConfirmDelete(false)} className={cls.btnGhost}>
              Cancel
            </button>
          </span>
        ) : (
          <button
            onClick={() => setConfirmDelete(true)}
            disabled={layout.isActive}
            className={`${cls.btnDanger} disabled:opacity-30 disabled:cursor-not-allowed`}
            title={layout.isActive ? 'Deactivate before deleting' : undefined}
          >
            Delete
          </button>
        )}
      </td>
    </tr>
  );
}
